import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  ShieldCheck,
  GraduationCap,
  Building2,
  CalendarDays,
  FileText,
  Upload,
  CheckCircle2
} from "lucide-react";

import "./profession.upload.css";
import API from "../../../api";

export default function ProfessionalUpload() {
  const navigate = useNavigate();

  const rawUser =
    localStorage.getItem("civilink_user");

  const user = rawUser
    ? JSON.parse(rawUser)
    : null;

  const [qualification, setQualification] =
    useState("");

  const [institution, setInstitution] =
    useState("");

  const [year, setYear] =
    useState("");

  const [certificateNumber, setCertificateNumber] =
    useState("");

  const [certificateFile, setCertificateFile] =
    useState("");

  const [fileName, setFileName] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  if (!user) {
    navigate("/");
    return null;
  }

  /* =====================================================
     UPLOAD CERTIFICATE
  ===================================================== */

  const uploadCertificate = (e) => {
    const file =
      e.target.files?.[0];

    if (!file) return;

    setFileName(file.name);

    const reader =
      new FileReader();

    reader.onload = () => {
      setCertificateFile(
        reader.result
      );
    };

    reader.readAsDataURL(file);
  };

  /* =====================================================
     SUBMIT VERIFICATION
  ===================================================== */

  const submitForVerification =
    async () => {

      if (
        !qualification ||
        !institution ||
        !year ||
        !certificateFile
      ) {
        alert(
          "Please fill all required fields."
        );

        return;
      }

      setLoading(true);

      try {

        const res =
          await API.post(
            "/profession/update",
            {
              userId: user._id,

              profession:
                user.profession ||
                "Engineer",

              skills: [],

              experienceYears:
                user.experienceYears ||
                0,

              profilePhoto:
                user.profilePhoto ||
                "",

              professionalVerification: {

                applied: true,

                status: "pending",

                documents: {

                  certificateType:
                    qualification,

                  certificateUrl:
                    certificateFile,

                  certificateNumber

                }

              }

            }
          );

        if (res.data.success) {

          localStorage.setItem(
            "civilink_user",
            JSON.stringify(
              res.data.user
            )
          );

          navigate(
            "/verification-status"
          );

        } else {

          alert(
            res.data.message ||
            "Submission failed"
          );

        }

      } catch (err) {

        console.error(
          "Verification submission failed",
          err
        );

        alert(
          "Submission failed. Please try again."
        );

      } finally {

        setLoading(false);

      }
    };


  /* =====================================================
     UI
  ===================================================== */

  return (
    <div className="upload-page">

      <div className="upload-wrapper">

        {/* =================================================
            HEADER
        ================================================= */}

        <header className="upload-header">

          <button
            type="button"
            className="upload-back"
            onClick={() =>
              navigate(-1)
            }
            aria-label="Go back"
          >
            <ArrowLeft size={19} />
          </button>

          <div>

            <span className="upload-brand">
              CIVILINK
            </span>

            <h1>
              Professional Verification
            </h1>

          </div>

        </header>


        {/* =================================================
            INTRO
        ================================================= */}

        <section className="upload-intro">

          <div className="upload-intro-icon">
            <ShieldCheck
              size={25}
            />
          </div>

          <div>

            <span className="upload-eyebrow">
              PROFESSIONAL IDENTITY
            </span>

            <h2>
              Verify your expertise
            </h2>

            <p>
              Submit your professional
              qualification so Civilink can
              review and verify your profile.
            </p>

          </div>

        </section>


        {/* =================================================
            VERIFICATION NOTE
        ================================================= */}

        <div className="verification-note">

          <ShieldCheck size={18} />

          <div>

            <strong>
              Your information is used
              for professional verification.
            </strong>

            <span>
              Only qualification-related
              documents are required.
            </span>

          </div>

        </div>


        {/* =================================================
            FORM
        ================================================= */}

        <section className="upload-card">

          <div className="upload-card-heading">

            <div>

              <span>
                QUALIFICATION DETAILS
              </span>

              <h2>
                Professional qualification
              </h2>

            </div>

            <GraduationCap
              size={22}
            />

          </div>


          {/* Qualification */}

          <div className="upload-field">

            <label>
              Degree / Qualification
              <span>*</span>
            </label>

            <div className="upload-input-wrap">

              <GraduationCap
                size={17}
              />

              <input
                type="text"
                placeholder="B.E Civil Engineering"
                value={qualification}
                onChange={(e) =>
                  setQualification(
                    e.target.value
                  )
                }
              />

            </div>

          </div>


          {/* Institution */}

          <div className="upload-field">

            <label>
              College / Institution
              <span>*</span>
            </label>

            <div className="upload-input-wrap">

              <Building2
                size={17}
              />

              <input
                type="text"
                placeholder="Anna University"
                value={institution}
                onChange={(e) =>
                  setInstitution(
                    e.target.value
                  )
                }
              />

            </div>

          </div>


          {/* Year */}

          <div className="upload-field">

            <label>
              Year of Passing
              <span>*</span>
            </label>

            <div className="upload-input-wrap">

              <CalendarDays
                size={17}
              />

              <input
                type="number"
                placeholder="2020"
                value={year}
                onChange={(e) =>
                  setYear(
                    e.target.value
                  )
                }
              />

            </div>

          </div>


          {/* Certificate number */}

          <div className="upload-field">

            <label>
              Certificate Number
              <small>
                Optional
              </small>
            </label>

            <div className="upload-input-wrap">

              <FileText
                size={17}
              />

              <input
                type="text"
                placeholder="Certificate number"
                value={
                  certificateNumber
                }
                onChange={(e) =>
                  setCertificateNumber(
                    e.target.value
                  )
                }
              />

            </div>

          </div>


          {/* =================================================
              DOCUMENT UPLOAD
          ================================================= */}

          <div className="upload-field">

            <label>
              Qualification Certificate
              <span>*</span>
            </label>

            <label className="certificate-upload">

              <input
                type="file"
                accept="image/*,.pdf"
                onChange={
                  uploadCertificate
                }
              />

              {fileName ? (

                <div className="uploaded-file">

                  <CheckCircle2
                    size={20}
                  />

                  <div>

                    <strong>
                      Document selected
                    </strong>

                    <span>
                      {fileName}
                    </span>

                  </div>

                </div>

              ) : (

                <div className="upload-placeholder">

                  <div className="upload-file-icon">
                    <Upload size={20} />
                  </div>

                  <div>

                    <strong>
                      Upload certificate
                    </strong>

                    <span>
                      PDF, JPG or PNG
                    </span>

                  </div>

                </div>

              )}

            </label>

          </div>


          {/* =================================================
              ACTIONS
          ================================================= */}

          <div className="upload-actions">

            <button
              type="button"
              className="upload-submit"
              disabled={loading}
              onClick={
                submitForVerification
              }
            >

              {loading
                ? "Submitting..."
                : "Submit for verification"}

              {!loading && (
                <ArrowRight
  size={17}
  className="submit-arrow"
/>
              )}

            </button>

            <button
              type="button"
              className="upload-cancel"
              onClick={() =>
                navigate(-1)
              }
            >
              Cancel
            </button>

          </div>

        </section>


        {/* =================================================
            FOOTER MESSAGE
        ================================================= */}

        <div className="upload-footer">

          <ShieldCheck size={15} />

          <span>
            Your profile remains pending until
            professional verification is completed.
          </span>

        </div>

      </div>

    </div>
  );
}